import React from 'react';
import { Text, View } from 'react-native';
import {
  applyDriverPlanFees,
  getRateTransitions,
  getEffectiveRates,
  getBillingUnit,
  isDiscountEffectivelyFree,
} from './common/utils/get-price-info';
import { ChargerAttributes } from '../models/charger-model';
import { generatePeakObj } from './common/utils/peak-off-peak-field';
import { UserPlanAssociation } from '../models/user-plan-associations-model';
import type { UserPricingOverride } from '../models/pricing-code-model';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

interface RateDisplayProps {
  renderCharger: ChargerAttributes;
}

/**
 * RateDisplay Component
 *
 * This component shows two rows:
 * - One for the Peak rate (only rendered if a peak price is provided and the schedule is valid)
 * - One for the Off-Peak rate.
 *
 * Next to each rate the extra time info is appended.
 */
const RateDisplay: React.FC<RateDisplayProps> = ({ renderCharger }) => {
  const peakData = generatePeakObj(renderCharger?.peak_selection);
  const now = new Date();
  const todayName = days[now.getDay()];
  const todaySchedule = peakData[todayName];
  const validPeakSchedule =
    todaySchedule && todaySchedule.start !== todaySchedule.end;
  const driverPlan: UserPlanAssociation | null = useSelector(
    (state: RootState) => state.user.driverPlan
  );

  const override = renderCharger?.user_pricing_override;
  const hasDiscount = override?.has_discount;
  const unit = getBillingUnit(renderCharger);

  const { peakTransition, offPeakTransition, unavailableTransition } =
    getRateTransitions(renderCharger);

  const { peak: basePeakRaw, offPeak: baseOffPeakRaw } = getEffectiveRates(renderCharger);
  const basePeakRate = applyDriverPlanFees(basePeakRaw || 0, driverPlan) || 0;
  const baseOffPeakRate = applyDriverPlanFees(baseOffPeakRaw || 0, driverPlan) || 0;

  const effectiveRates = hasDiscount ? getEffectiveRates(renderCharger, override) : null;

  const hasPeakRate = basePeakRaw > 0 && validPeakSchedule;

  return (
    <View>
      {hasDiscount && (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6, backgroundColor: '#10B981' + '20', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4 }}>
          <Text style={{ color: '#10B981', fontSize: 12, fontWeight: '700' }}>
            {isDiscountEffectivelyFree(override) ? 'FREE' : override.discount_type === 'percentage' ? `${override.discount_value}% Off` : `$${override.discount_value.toFixed(3)}${unit}`}
          </Text>
          <Text style={{ color: '#8D8E90', fontSize: 11, marginLeft: 6 }}>
            via {override.source_name}
          </Text>
        </View>
      )}

      {hasPeakRate ? (
        <View className="flex flex-row justify-between items-center">
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text className="text-base font-semibold text-white">
              Peak Rate*
            </Text>
            {peakTransition ? (
              <Text
                style={{
                  color: '#39BEB7',
                  fontSize: 12,
                  marginLeft: 4,
                  fontWeight: 'bold',
                }}
              >
                {`[${peakTransition}]`}
              </Text>
            ) : null}
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {hasDiscount && (
              <Text style={{ color: '#8D8E90', fontSize: 14, textDecorationLine: 'line-through', marginRight: 6 }}>
                {`$${basePeakRate.toFixed(3)}`}
              </Text>
            )}
            <Text className="text-base font-semibold" style={hasDiscount ? { color: '#10B981' } : { color: 'white' }}>
              {hasDiscount
                ? `$${(effectiveRates?.peak || 0).toFixed(3)}${unit}`
                : `$${basePeakRate.toFixed(3)}${unit}`}
            </Text>
          </View>
        </View>
      ) : null}

      <View className="flex flex-row justify-between items-center">
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text className="text-base font-semibold text-white">
            {hasPeakRate
              ? 'Off-Peak Rate*'
              : 'Rate*'}
          </Text>
          {offPeakTransition ? (
            <Text
              style={{
                color: '#39BEB7',
                fontSize: 12,
                marginLeft: 4,
                fontWeight: 'bold',
              }}
            >
              {`[${offPeakTransition}]`}
            </Text>
          ) : null}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {hasDiscount && (
            <Text style={{ color: '#8D8E90', fontSize: 14, textDecorationLine: 'line-through', marginRight: 6 }}>
              {`$${baseOffPeakRate.toFixed(3)}`}
            </Text>
          )}
          <Text className="text-base font-semibold" style={hasDiscount ? { color: '#10B981' } : { color: 'white' }}>
            {hasDiscount
              ? `$${(effectiveRates?.offPeak || 0).toFixed(3)}${unit}`
              : `$${baseOffPeakRate.toFixed(3)}${unit}`}
          </Text>
        </View>
      </View>

      <View className="flex flex-row justify-between items-center">
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text
            style={{
              color: '#39BEB7',
              fontSize: 12,
              marginLeft: 4,
              fontWeight: 'bold',
            }}
          >
            *NOTE: Rates are all-inclusive with fees before taxes
          </Text>
        </View>
      </View>

      {unavailableTransition ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: 6,
            backgroundColor: '#92400E',
            borderRadius: 6,
            paddingHorizontal: 8,
            paddingVertical: 4,
          }}
        >
          <Text style={{ color: '#FCD34D', fontSize: 12, fontWeight: '600' }}>
            {unavailableTransition}
          </Text>
        </View>
      ) : null}
    </View>
  );
};

export default RateDisplay;
